import { TAGS } from '@mollusk/shared';
import Alert from '@mui/material/Alert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { api } from '../api/endpoints';
import { useIsAdmin } from '../features/auth/internal';
import { TagsView } from '../features/tags/TagsView';

function TagsPage() {
  const { tag: fromUrl } = Route.useSearch();
  const tag = fromUrl && TAGS.some((t) => t.tag === fromUrl) ? fromUrl : 'new_build';
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const stats = useQuery({ queryKey: ['tag-stats'], queryFn: api.tagStats, staleTime: 3_600_000 });
  const examples = useQuery({
    queryKey: ['tag-examples', tag],
    queryFn: () => api.tagExamples(tag),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['tag-examples', tag] });
  const pin = useMutation({
    mutationFn: (id: string) => api.pinTagExample(tag, id),
    onSuccess: refresh,
  });
  const unpin = useMutation({ mutationFn: () => api.unpinTagExample(tag), onSuccess: refresh });
  return (
    <>
      {pin.error instanceof Error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn't pin: {pin.error.message}
        </Alert>
      ) : null}
      <TagsView
        key={tag}
        tag={tag}
        stats={stats.data}
        examples={examples.data}
        isAdmin={isAdmin}
        onTag={(t) => navigate({ to: '/tags', search: { tag: t } })}
        onPin={(id) => pin.mutate(id)}
        onUnpin={() => unpin.mutate()}
      />
    </>
  );
}

export const Route = createFileRoute('/tags')({
  validateSearch: z.object({ tag: z.string().optional() }),
  component: TagsPage,
});
